import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'

import { kitsFetch, kitsStateClear } from 'app/actions/orgs/kits'
import { kitsNeedUpdate } from 'app/actions/orgs/kits/options'

import { Kits2ExtendedItems, type Complex } from './kits2ExtendedItemsList'
import useKits from '../useKits'
import type { RootState } from 'app/store' // экспортировать: export type RootState = ReturnType<typeof store.getState>;
import { DietFoodTypeId } from 'app/dataTypes/org/dietFoodTypes'
/**
 *  Данные для расширенного отображения комплектов
 */

type Params = {
  date: string
  isNeedToRequest?: boolean
  buildingId?: number
  dietFoodTypeId?: DietFoodTypeId
  useComplexBuildingVariants?: boolean
  onSuccessRequest: () => void
}

export function useOrgsKitsExtended(
  orgId: string,
  {
    date,
    isNeedToRequest,
    buildingId,
    dietFoodTypeId,
    useComplexBuildingVariants,
    onSuccessRequest,
  }: Params,
) {
  const dispatch = useDispatch()
  const { loading, needUpdate } = useSelector(
    (state: RootState) => state.orgs.kits.options,
    shallowEqual,
  )

  const kits = useKits()
  const items = useMemo(() => Kits2ExtendedItems(kits), [kits])

  const fetchKits = useCallback(() => {
    dispatch(
      kitsFetch(
        orgId,
        {
          date,
          deleted: false,
          visible: true,
          include_subsidy_orders: true,
          building_id: buildingId,
          diet_food_type_id: dietFoodTypeId ? parseInt(dietFoodTypeId, 10) : undefined,
        },
        true,
      ),
    )
  }, [buildingId, date, dietFoodTypeId, dispatch, orgId])

  useEffect(() => {
    if (needUpdate) {
      dispatch(kitsNeedUpdate(false))
    }
    if (useComplexBuildingVariants) {
      if (buildingId) {
        fetchKits()
      }
      return
    } else {
      fetchKits()
    }
  }, [
    needUpdate,
    date,
    orgId,
    fetchKits,
    dispatch,
    useComplexBuildingVariants,
    buildingId,
    dietFoodTypeId,
  ])

  useEffect(() => {
    if (isNeedToRequest) {
      fetchKits()
      onSuccessRequest()
    }
  }, [isNeedToRequest, fetchKits, onSuccessRequest])

  useEffect(
    () => () => {
      dispatch(kitsStateClear())
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  )

  return { items, loading, refresh: fetchKits }
}
